import { Injectable } from '@angular/core';
import { Toast } from '@app/shared/toasts/toast';
import { StringMap } from '@app/utils/string-map';
import { Observable, of } from 'rxjs';

/**
 * Custom implementation of error toasts because using the normal toaster service leads to an
 * infinite error loop for template errors (or really any error which is caused at a place that is
 * accessed by the change detection).
 *
 * e.g. if a template causes an error because a null property is accessed, then the compiler will
 * throw an error and the AppErrorHandler wants to show a toast using the ToastService. Showing a
 * toast will cause the change detection to run again and this will cause the template to be
 * evaluated again as well. This will throw another error and there we have the infinite error loop.
 *
 * See the github issue for more information: https://github.com/angular/angular/issues/17010
 */
@Injectable({
  providedIn: 'root',
})
export class ErrorToastService {
  private container: HTMLElement | null = null;
  private enabled = true;
  private toastMap: StringMap<Toast> = {};

  /**
   * Hide all currently visible toasts.
   */
  clear(): void {
    Object.values(this.toastMap).forEach((toast) => {
      toast.hide(true);
    });

    this.toastMap = {};
  }

  /**
   * Disable showing future toasts.
   */
  disable(): void {
    this.enabled = false;
  }

  /**
   * Enable showing toasts.
   */
  enable(): void {
    this.enabled = true;
  }

  /**
   * Show a toast with the given title and message.
   * This will have no effect if the service is disabled.
   */
  show(title: string, message?: string): Observable<void> {
    let toast$: Observable<void> = of(undefined);

    if (this.enabled) {
      const container = document.querySelector('.portal-toasts');
      const toast = new Toast(title, message, () => {
        if (this.container?.childNodes.length === 0) {
          document.body.removeChild(this.container.parentNode);
          this.container = null;
        }
      });

      container.insertBefore(toast.ref, container.children[0]);

      this.toastMap[toast.id] = toast;

      toast$ = toast.show();
    }

    return toast$;
  }
}
